/**
 * Task 16 — surface permanent outbox push failures (docs/08 §4, §10).
 * Shows table + row id + error code only in the status line; payload is
 * recoverable via data-testid for the owning user, never logged.
 */

import type { OutboxEntry } from '../../schemas/local-only';

export type SyncStatusProps = {
  pendingCount: number;
  permanentFailures: readonly OutboxEntry[];
  conflictCount: number;
};

export function SyncStatus({
  pendingCount,
  permanentFailures,
  conflictCount,
}: SyncStatusProps): JSX.Element | null {
  if (pendingCount === 0 && permanentFailures.length === 0 && conflictCount === 0) {
    return null;
  }

  return (
    <section
      data-testid="sync-status"
      className="rounded-lg border border-slate-800 bg-slate-900/60 p-4"
      aria-live="polite"
    >
      <h2 className="text-sm font-medium text-slate-300">Sync</h2>
      <p className="mt-1 text-sm text-slate-400" data-testid="sync-pending-count">
        Pending outbox: {pendingCount}
      </p>
      {conflictCount > 0 ? (
        <p className="mt-1 text-sm text-amber-400" data-testid="sync-conflict-count">
          Conflicts needing review: {conflictCount}
        </p>
      ) : null}
      {permanentFailures.length > 0 ? (
        <ul className="mt-3 space-y-2" data-testid="sync-permanent-failures">
          {permanentFailures.map((entry) => (
            <li
              key={entry.id}
              className="text-sm text-rose-300"
              data-testid="sync-permanent-failure"
              data-table={entry.table}
              data-row-id={entry.rowId}
              data-error={entry.lastError ?? 'permanent_failure'}
            >
              <span>
                Push stopped for {entry.table} ({entry.lastError ?? 'permanent_failure'}).
              </span>
              <pre
                className="mt-1 max-h-24 overflow-auto rounded bg-slate-950/80 p-2 text-xs text-slate-500"
                data-testid="sync-failure-payload"
              >
                {JSON.stringify(entry.payload)}
              </pre>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
